import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@cockpit/ui'
import * as tenantsApi from '@/lib/api/tenants'
import { queryKeys } from '@/lib/queryKeys'
import { formatCents } from '@/lib/formatters'
import { selectIsSuperAdmin, useAuthStore } from '@/stores/authStore'
import { useTenantStore } from '@/stores/tenantStore'
import { TenantDot } from './TenantDot'

export function TenantSwitcher({ compact = false }: { compact?: boolean }) {
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin)
  if (isSuperAdmin) return <SuperAdminSwitcher compact={compact} />
  return <TenantAdminChip compact={compact} />
}

function CapLine({ capCents }: { capCents: number | null | undefined }) {
  const { t } = useTranslation('common')
  if (capCents == null) {
    return <span className="text-[11px] text-muted-foreground">{t('tenant.noCap')}</span>
  }
  return (
    <span className="text-[11px] text-muted-foreground">
      {t('tenant.cap', { amount: formatCents(capCents) })}
    </span>
  )
}

function shortName(name: string): string {
  const first = name.trim().split(/\s+/)[0]
  return first && first.length > 0 ? first : name
}

function TenantAdminChip({ compact }: { compact: boolean }) {
  const admin = useAuthStore((s) => s.admin)
  const { data } = useQuery({
    queryKey: queryKeys.tenants.list(),
    queryFn: () => tenantsApi.list(),
    enabled: admin?.tenantId != null,
  })
  const tenant = data?.tenants.find((t) => t.id === admin?.tenantId)
  const name = tenant?.name ?? '—'

  if (compact) {
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <div className="rail-cell text-muted-foreground" aria-label={name}>
            {tenant ? (
              <TenantDot slug={tenant.slug} className="rail-dot" />
            ) : (
              <Building2 className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            )}
            <span className="rail-caption">{shortName(name)}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" sideOffset={10} className="font-medium">
          {name}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 py-2 text-[13px]">
      {tenant && <TenantDot slug={tenant.slug} className="h-2 w-2" />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-medium leading-tight">{name}</div>
        <CapLine capCents={tenant?.monthlyCostCapCents} />
      </div>
    </div>
  )
}

function SuperAdminSwitcher({ compact }: { compact: boolean }) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const setActiveTenant = useTenantStore((s) => s.setActiveTenant)

  const { data } = useQuery({
    queryKey: queryKeys.tenants.list(),
    queryFn: () => tenantsApi.list(),
  })
  const tenants = data?.tenants ?? []
  const active = tenants.find((tenant) => tenant.id === activeTenantId)
  const label = active?.name ?? t('tenant.all')
  const caption = active ? shortName(active.name) : t('tenant.allShort')

  const select = (id: string | null) => {
    setActiveTenant(id)
    setOpen(false)
  }

  const list = (
    <Command>
      <CommandInput placeholder={t('tenant.all')} />
      <CommandList>
        <CommandEmpty>{t('errors.notFound')}</CommandEmpty>
        <CommandGroup>
          <CommandItem value="__all__" onSelect={() => select(null)}>
            <Check
              className={cn('mr-2 h-4 w-4', activeTenantId === null ? 'opacity-100' : 'opacity-0')}
            />
            {t('tenant.all')}
          </CommandItem>
          {tenants.map((tenant) => (
            <CommandItem key={tenant.id} value={tenant.name} onSelect={() => select(tenant.id)}>
              <Check
                className={cn(
                  'mr-2 h-4 w-4',
                  activeTenantId === tenant.id ? 'opacity-100' : 'opacity-0',
                )}
              />
              <TenantDot slug={tenant.slug} className="mr-2" />
              {tenant.name}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )

  if (compact) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={open}
                aria-label={label}
                className="rail-cell text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                {active ? (
                  <TenantDot slug={active.slug} className="rail-dot" />
                ) : (
                  <Building2 className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                )}
                <span className="rail-caption">{caption}</span>
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          {!open ? (
            <TooltipContent side="right" align="center" sideOffset={10} className="font-medium">
              {label}
            </TooltipContent>
          ) : null}
        </Tooltip>
        <PopoverContent className="w-[200px] p-0" align="center" side="right" sideOffset={10}>
          {list}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="h-auto w-full justify-between gap-2 py-2"
        >
          <span className="inline-flex min-w-0 items-center gap-2">
            {active ? <TenantDot slug={active.slug} /> : null}
            <span className="flex min-w-0 flex-col items-start text-left">
              <span className="truncate text-[12.5px] font-medium">{label}</span>
              {active ? <CapLine capCents={active.monthlyCostCapCents} /> : null}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[196px] p-0" align="start">
        {list}
      </PopoverContent>
    </Popover>
  )
}
